import CloseCircle from '@betfan/icons/linear/EssentionalUI/CloseCircle.svg?react';
import cn from 'classnames';
import { memo, ReactElement, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CommonAPI } from '../../api';
import { RegistrationPromotionResponseDto } from '../../api/common.generated';
import { DSInput, DSSpinner } from '../../designSystem';
import { addNotification } from '../../shared/components/toast';
import { createLogger } from '../../shared/logger';
import { findCookieValue } from '../../shared/util/utility';
import { PromotionListItem } from './components/PromotionListTemplate';
import { PromotionPickerType } from './PromotionPicker.types';
import { promotionPickerTheme, resolveSelectedPromotion } from './PromotionPicker.utils';

export const PromotionPicker = memo(({ theme = 'green', initialCode, initialPromotion, onCodeChange, onChangePromotion }: PromotionPickerType) => {
	const logger = createLogger('PromotionPicker');
	const cookiePromoCode = findCookieValue('promo');
	const styles = promotionPickerTheme(theme);
	const { t } = useTranslation();
	const [data, setData] = useState({
		inputCode: '',
		usedCode: '',
		selectedPromotion: {
			id: '',
			name: '',
		},
	});
	const [loading, setLoading] = useState<boolean>(true);
	const [promotions, setPromotions] = useState<RegistrationPromotionResponseDto[]>([]);

	const setUsedCode = (code: string) => {
		setData((prevState) => ({ ...prevState, inputCode: code, usedCode: code }));
		onCodeChange(code);
		logger.log('setUsedCode', code);
	};
	const clearUsedCode = () => {
		setData((prevState) => ({ ...prevState, inputCode: '', usedCode: '' }));
		onCodeChange('');
		checkCodeAvailable('');
		logger.log('clearUsedCode');
	};

	const setInputCode = (code: string) => {
		setData((prevState) => ({ ...prevState, inputCode: code, usedCode: '' }));
		onCodeChange('');
	};

	const checkCodeAvailable = (code: string, promotion?: { id: string; name: string }) => {
		setLoading(true);
		CommonAPI.Cms.getRegistrationPromotions({ code })
			.then(({ data }) => {
				const promotionWithCode = data.promotions.find((promotion) => promotion.existCode);
				const selectedPromotion = resolveSelectedPromotion({
					initialPromotion: promotion,
					promotionWithCode,
					fetchedPromotions: data.promotions,
				});

				let sortedPromotions = [...data.promotions];

				if (promotionWithCode) {
					sortedPromotions = [promotionWithCode, ...data.promotions.filter((p) => p.promotionId !== promotionWithCode.promotionId)];

					addNotification('success', t('regPromotionPickerToastSuccess'));
					setData({
						selectedPromotion: {
							id: selectedPromotion.id,
							name: selectedPromotion.name,
						},
						inputCode: code,
						usedCode: code,
					});
					onCodeChange(code);
				} else {
					setData((prevState) => ({
						...prevState,
						selectedPromotion: {
							id: selectedPromotion.id,
							name: selectedPromotion.name,
						},
					}));
				}
				setUsedCode(code);
				setPromotions(sortedPromotions);
			})
			.catch(() => {
				addNotification('error', t('regPromotionPickerPromotionFetchError'));
			})
			.finally(() => {
				setLoading(false);
			});
	};

	const inputRightIcon = (): ReactElement => {
		if (loading) {
			return <DSSpinner className={cn('  size-4', styles.iconSpinner)} />;
		} else if (data.usedCode) {
			return (
				<div onClick={clearUsedCode} className={'cursor-pointer'}>
					<CloseCircle className={cn('w-full h-full', styles.iconInput)} />
				</div>
			);
		} else {
			return data.inputCode ? (
				<div
					onClick={() => checkCodeAvailable(data.inputCode)}
					className={cn('cursor-pointer absolute right-0 p-1 tracking-widest text-paragraph-xs font-semibold', styles.inputUseColor)}
				>
					{t('regPromotionPickerInputUse')}
				</div>
			) : (
				<></>
			);
		}
	};
	const promotionsListTemplate = () => {
		return promotions.map((promotion, index) => (
			<PromotionListItem
				styles={styles}
				selectedPromotionId={data.selectedPromotion.id}
				onSelect={(promotionId) =>
					setData((prevState) => ({ ...prevState, selectedPromotion: { id: promotionId, name: promotion.promotionName } }))
				}
				promotion={promotion}
				key={index}
			/>
		));
	};
	const listHeadline = () => {
		if (data.usedCode && promotions.length) {
			return t('regPromotionPickerAvailable');
		} else if (promotions.length) {
			return t('regPromotionPickerChosen');
		}
	};
	useEffect(() => {
		onChangePromotion(data.selectedPromotion);
	}, [data.selectedPromotion]);

	useEffect(() => {
		checkCodeAvailable(initialCode ?? cookiePromoCode, initialPromotion);
	}, []);

	return (
		<>
			<DSInput
				disabled={loading || !!data.usedCode}
				color={styles.inputColor}
				autoFocus
				label={t('promoCodes')}
				value={data.inputCode}
				onChange={(newValue) => setInputCode(newValue)}
				rightIcon={inputRightIcon()}
				cyTestRef="reg-code"
				cyErrorRef="reg-validation-error"
			/>
			<div className={cn('text-paragraph-md font-semibold  mt-4', styles?.textColor)}>{listHeadline()}</div>
			<div className="relative">
				<div className={cn('absolute left-0 top-0 right-0 h-3 z-1 bg-gradient-to-b ', styles?.gradientFrom)} />
				<div className="h-[calc(100vh-420px)] overflow-hidden overflow-y-auto py-2">{!loading && promotionsListTemplate()}</div>
				<div className={cn('absolute left-0 bottom-0 right-0 h-3 z-1  bg-gradient-to-t ', styles?.gradientFrom)} />
			</div>
		</>
	);
});
